import { useCallback, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { ticketDetailsShortcutMap } from 'config/keyboardShortcuts';
import { StatusIdE } from 'declarations/enums/tickets';
import { buildShortcutBindings } from 'helpers/shortcuts';

import type { ChangeTicketDetailsFormValuesT } from './useChangeTicketDetailsForm';
import useTicketDetailsPopup from './useTicketDetailsPopup';

type UseTicketDetailsShortcutsT = {
  handleSubmitTicketDetailsForm: (data: ChangeTicketDetailsFormValuesT) => void;
  formMethods: UseFormReturn<ChangeTicketDetailsFormValuesT>;
  handleClose?: () => void;
  isAnyModalOpen?: boolean;
};

export function useTicketDetailsShortcuts({
  handleSubmitTicketDetailsForm,
  formMethods,
  handleClose,
  isAnyModalOpen = false,
}: UseTicketDetailsShortcutsT): { bindings: ReturnType<typeof buildShortcutBindings> } {
  const {
    ticketDetails,
    handleCloseTicketDetailsPopup,
    handleOpenReassignTicketPopup,
    handleOpenShareTicketPopup,
    handleOpenAddAttachmentPopup,
    handleOpenTicketApprovePopup,
    handleOpenTicketClosePopup,
    handleOpenTicketReopenPopup,
  } = useTicketDetailsPopup();

  const baseClose = handleClose ?? handleCloseTicketDetailsPopup;
  const closeTicketDetails = useCallback((): void => {
    if (isAnyModalOpen) return;
    baseClose();
  }, [isAnyModalOpen, baseClose]);

  const save = useCallback((): void => {
    formMethods.handleSubmit(handleSubmitTicketDetailsForm)();
  }, [formMethods, handleSubmitTicketDetailsForm]);

  const closeOrReopenTicket = useCallback((): void => {
    if (!ticketDetails) return;
    const isOpen =
      ticketDetails.statusId === StatusIdE.Open || ticketDetails.statusId === StatusIdE.ReOpened;
    if (isOpen) {
      handleOpenTicketClosePopup();
    } else {
      handleOpenTicketReopenPopup();
    }
  }, [ticketDetails, handleOpenTicketClosePopup, handleOpenTicketReopenPopup]);

  const bindings = useMemo(
    () =>
      buildShortcutBindings(ticketDetailsShortcutMap.shortcuts, {
        save,
        closeTicketDetails,
        reassign: handleOpenReassignTicketPopup,
        share: handleOpenShareTicketPopup,
        addAttachment: handleOpenAddAttachmentPopup,
        getApproval: handleOpenTicketApprovePopup,
        closeOrReopenTicket,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- closeOrReopenTicket captures handleOpenTicketReopenPopup/handleOpenTicketClosePopup
    [
      save,
      closeTicketDetails,
      handleOpenReassignTicketPopup,
      handleOpenShareTicketPopup,
      handleOpenAddAttachmentPopup,
      handleOpenTicketApprovePopup,
      closeOrReopenTicket,
    ]
  );

  return { bindings };
}
